import { StyledButton } from '@elements/Button';
import { MuteText } from '@elements/Common';
import { NumberMaskInput } from '@elements/CustomMaskInput';
import { Flag } from '@elements/Flag';
import { DarkPopup } from '@elements/Popup';
import { Velocities } from '@page-components/condensed-timeline';
import { CommonUtility } from '@utility/common';
import { ErrorMessageConstant } from '@utility/constants/common';
import PredictionService from '@utility/services/prediction';
import StorageProviderService from '@utility/services/storage-provider';
import { ToastMessage } from '@utility/toast';
import { CaretLeft, Info } from '@phosphor-icons/react';
import React, {
  type HTMLAttributes,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Badge,
  cn,
  RadioIndicator,
  type BadgeProps,
} from '@skupreme/shared/ui';
import { useOutsideAlerter } from '@hooks/outside-click';

const TRUNCATE =
  'sk:relative sk:inline-block sk:max-w-[100px] sk:truncate sk:align-bottom';

function SmallBadge({ className, ...props }: BadgeProps) {
  return (
    <Badge
      {...props}
      className={cn('sk:px-1.5 sk:py-0 sk:text-[10px]', className)}
    />
  );
}

export function POWindowVelocityRenderer({
  data,
  hardRefreshLoading,
  context,
  loading,
  setLoading,
  refreshTimeline,
  itemId,
  title,
}) {
  const [isOriginSelected, setOriginSelected] = useState('');
  const [manualVelocities, setManualVelocities] = useState({});
  const [oldManualVelocities, setOldManualVelocities] = useState({});
  const [providerVelocities, setProviderVelocities] = useState([]);
  const [selectedVelocity, setSelectedVelocity] = useState(-1);
  // onclose setSelectedVelocity(-1);

  const wrapperRef = useRef(null);
  useOutsideAlerter(wrapperRef, () => {
    setSelectedVelocity(-1);
  });

  useEffect(() => {
    if (!CommonUtility.isValidObject(data)) return;

    const velocities = [];
    let manualVelocities = {};

    const primaryHybridProvider = data.storageProviders?.find(
      x =>
        x.is_primary_hybrid &&
        x.fulfillment_provider_id === data?.primary_warehouse,
    );

    for (let i = 0; i < data.storageProviders?.length; i += 1) {
      const {
        is_warehouse,
        is_fulfillable,
        fulfillment_provider_id,
        is_primary_hybrid,
      } = data.storageProviders[i];

      if (!fulfillment_provider_id) continue;

      const isPrimary = data.primary_warehouse === fulfillment_provider_id;
      if (is_warehouse && !is_fulfillable && !isPrimary) {
        continue;
      }

      manualVelocities = {
        ...manualVelocities,
        [fulfillment_provider_id]: {
          value:
            data.storageProviders[i].providerSalesVelocities?.manualSales
              ?.value || '',
          changed: false,
        },
      };

      if (is_primary_hybrid) continue;

      if (data.storageProviders[i].providerSalesVelocities) {
        const velocityObject = {
          ...data.storageProviders[i],
          ...data.storageProviders[i].providerSalesVelocities,
          adjustedSalesVelocity: data.storageProviders[i].adjustedSalesVelocity,
          displayName: data.storageProviders[i].fulfillment_provider_name,
          fulfillment_provider_id:
            data.storageProviders[i].fulfillment_provider_id,
        };
        if (primaryHybridProvider && isPrimary) {
          velocityObject.hybridProvider = {
            ...primaryHybridProvider,
            ...primaryHybridProvider.providerSalesVelocities,
            adjustedSalesVelocity: primaryHybridProvider.adjustedSalesVelocity,
            displayName: primaryHybridProvider.fulfillment_provider_name,
            fulfillment_provider_id:
              primaryHybridProvider.fulfillment_provider_id,
          };
        }

        // @ts-expect-error - TS2345: Argument of type 'any' is not assignable to parameter of type 'never'.
        velocities.push(velocityObject);
      }
    }

    setProviderVelocities(velocities);
    setManualVelocities(manualVelocities);
    setOldManualVelocities(manualVelocities);
  }, [data]);

  useEffect(() => {
    setOriginSelected('');
  }, [itemId]);

  const mappedVelocities = useMemo(
    () =>
      providerVelocities.filter(d => {
        const { fulfillment_provider_id } = d;
        return isOriginSelected === fulfillment_provider_id;
      }),
    [providerVelocities, isOriginSelected],
  );

  const updateVelocity = providerId => {
    updateManualVelocity(
      providerId,
      manualVelocities[providerId]?.value,
      false,
    );
  };

  const updateWindowVelocity = async payload => {
    setLoading('Updating');
    try {
      await StorageProviderService.updateWindowVelocity(itemId, {
        default_velocity_window: [payload],
      });
      await refreshTimeline();

      ToastMessage.success('Velocity Updated Successfully');
    } catch (error) {
      setLoading(false);
      ToastMessage.apiError(ErrorMessageConstant.promptToChat);
    } finally {
      setSelectedVelocity(-1);
    }
  };

  const pushSelectedVelocity = (index, providerId) => {
    if (index < 0) return;
    setSelectedVelocity(index);

    const destProvider = providerVelocities.find(
      // @ts-expect-error - TS2339: Property 'fulfillment_provider_id' does not exist on type 'never'.
      x => x.fulfillment_provider_id === providerId,
    );

    /* @ts-expect-error - TS2339: Property 'hybridProvider' does not exist on type 'never'. */
    const checkDataIn = destProvider?.hybridProvider || destProvider || {};

    const orgVelocities = Object.keys(checkDataIn)?.filter(x =>
      x.includes('Day'),
    );
    updateWindowVelocity({
      days: orgVelocities[index],
      provider_id: providerId,
    });
  };

  const updateManualVelocity = async (
    fulfillment_provider_id,
    prediction,
    reset = false,
    shouldRefresh = false,
    startMsg = null,
    endMsg = true,
  ) => {
    setLoading(startMsg || 'Updating Prediction Values');
    const payload = {
      item_id: itemId,
      addManual: !reset,
      fulfillment_provider_id,
      prediction: reset ? '0' : prediction,
    };
    // @ts-expect-error - TS2790: The operand of a 'delete' operator must be optional.
    if (shouldRefresh) delete payload.addManual;
    try {
      await PredictionService.updateVelocity(payload);
      await refreshTimeline();
      if (endMsg) {
        ToastMessage.success('Velocity Updated Successfully');
      }
      setSelectedVelocity(-1);
    } catch (error) {
      ToastMessage.apiError(ErrorMessageConstant.promptToChat);
      setLoading(false);
    }
  };

  const setManualInputValues = (name, value) => {
    setManualVelocities({
      ...manualVelocities,
      [name]: {
        value,
        changed: oldManualVelocities[name]
          ? oldManualVelocities[name].value !== value
          : true,
      },
    });
  };

  const windowSelectedVelocity = useMemo(() => {
    if (data?.defaultVelocityWindow) {
      return data?.defaultVelocityWindow[isOriginSelected];
    }
    return null;
  }, [data, isOriginSelected]);

  return (
    <div
      className={cn(
        'sk:w-full',
        mappedVelocities.length < 4 ? 'sk:justify-start' : 'sk:justify-between',
      )}
    >
      {!CommonUtility.isValidArray(mappedVelocities) ? (
        /* A caller that frames the block in its own card passes no title. */
        title ? (
          <h4>{title}</h4>
        ) : null
      ) : (
        <StorageProviderQuickView
          providerVelocities={providerVelocities}
          isOriginSelected={isOriginSelected}
          primaryWarehouse={data?.primary_warehouse}
          closeDetailView={() => setOriginSelected('')}
        />
      )}
      <StorageProviderRenderer
        mappedVelocities={mappedVelocities}
        providerVelocities={providerVelocities}
        isOriginSelected={isOriginSelected}
        setOriginSelected={setOriginSelected}
        data={data}
        allVelocities={providerVelocities}
      />
      <div className="sk:mt-2">
        {mappedVelocities.map(d => (
          <VelocityDetail
            d={d}
            manualVelocities={manualVelocities}
            oldManualVelocities={oldManualVelocities}
            hardRefreshLoading={hardRefreshLoading}
            updateWindowVelocity={updateWindowVelocity}
            setSelectedVelocity={setSelectedVelocity}
            pushSelectedVelocity={pushSelectedVelocity}
            // @ts-expect-error - TS2322: Type '{ d: never; manualVelocities: {}; oldManualVelocities: {}; hardRefreshLoad...
            context={context}
            selectedVelocity={selectedVelocity}
            loading={loading}
            setManualInputValues={setManualInputValues}
            updateVelocity={updateVelocity}
            updateManualVelocity={updateManualVelocity}
            windowSelectedVelocity={windowSelectedVelocity}
          />
        ))}
      </div>
    </div>
  );
}

function StorageProviderRenderer({
  mappedVelocities,
  providerVelocities,
  isOriginSelected,
  setOriginSelected,
  data,
  allVelocities,
}) {
  const primaryProvider = useMemo(
    () =>
      providerVelocities?.find(
        x => x.fulfillment_provider_id === data?.primary_warehouse,
      ),
    [data, providerVelocities],
  );

  if (isOriginSelected) return <></>;

  return (
    <div
      className={cn(
        'sk:relative sk:flex sk:max-w-fit sk:min-w-[350px] sk:flex-wrap sk:gap-2.5',
        providerVelocities.length <= 1
          ? ''
          : 'sk:mt-2.5 sk:rounded-[5px] sk:border sk:border-solid sk:border-border sk:p-3',
      )}
    >
      {providerVelocities.length > 1 && (
        <PrimaryProviderHeader
          allVelocities={allVelocities}
          provider={primaryProvider}
        />
      )}
      {providerVelocities.map(d => (
        <VelocityQuickView
          provider={d}
          isOriginSelected={isOriginSelected}
          // @ts-expect-error - TS2322: Type '{ provider: any; isOriginSelected: any; mappedVelocities: any; setOriginSe...
          mappedVelocities={mappedVelocities}
          setOriginSelected={setOriginSelected}
          isPrimary={data?.primary_warehouse === d?.fulfillment_provider_id}
          allVelocities={allVelocities}
        />
      ))}
    </div>
  );
}

function PrimaryProviderHeader({ provider, allVelocities }) {
  const {
    fulfillment_provider_name,
    marketplaceRegionName,
    providerSalesVelocity,
    adjustedSalesVelocity,
    hybridProvider,
  } = useMemo(() => provider || {}, [provider]);

  const isAdjusted = useMemo(
    () =>
      CommonUtility.isNotNull(adjustedSalesVelocity) &&
      providerSalesVelocity !== adjustedSalesVelocity,
    [adjustedSalesVelocity, providerSalesVelocity],
  );

  const filteredArray = useMemo(
    () =>
      allVelocities.filter(
        x =>
          (x.fulfillment_provider_id !== provider?.fulfillment_provider_id &&
            (x.providerSalesVelocity || x.adjustedSalesVelocity)) ||
          !!x.unknownProvider,
      ),
    [provider, allVelocities],
  );

  return (
    <div className="sk:absolute sk:-top-2.5 sk:left-1/2 sk:flex sk:-translate-x-1/2 sk:bg-surface sk:px-2.5 sk:text-xs sk:whitespace-nowrap">
      <Flag height={17} name={marketplaceRegionName} />
      <span className="sk:flex sk:items-center">
        <span className={cn(TRUNCATE, 'sk:mr-1')}>
          {fulfillment_provider_name}
        </span>
        {isAdjusted && <MuteText className="sk:mr-1">(Adjusted)</MuteText>}
        Unified Velocity
        <DarkPopup
          position="top center"
          trigger={<Info className="sk:-mb-0.5 sk:ml-0.5" />}
        >
          <PrimaryProviderBreakdown
            hybridProvider={hybridProvider}
            provider={provider}
            filteredArray={filteredArray}
          />
        </DarkPopup>
      </span>
      <div className="sk:flex sk:pl-2">
        <span className="sk:pr-1">
          {adjustedSalesVelocity || providerSalesVelocity}
        </span>
        <MuteText>Units / Day</MuteText>
      </div>
    </div>
  );
}

function StorageProviderQuickView({
  providerVelocities,
  isOriginSelected,
  primaryWarehouse,
  closeDetailView,
}) {
  const currentProvider = useMemo(
    () =>
      providerVelocities.find(
        x => x.fulfillment_provider_id === isOriginSelected,
      ),
    [isOriginSelected],
  );

  const {
    fulfillment_provider_name,
    marketplaceRegionName,
    present_inventory,
    fulfillment_provider_id,
  } = useMemo(() => currentProvider, [currentProvider]);

  return (
    <div className="sk:flex sk:items-center">
      <button
        type="button"
        aria-label="Back to all storage providers"
        className="sk:flex sk:cursor-pointer sk:border-0 sk:bg-transparent sk:p-0 sk:text-fg"
        onClick={closeDetailView}
      >
        <CaretLeft />
      </button>
      <h4 className="sk:m-0 sk:mx-2">Editing Velocity for</h4>
      <Flag name={marketplaceRegionName} />
      <h4 className="sk:m-0">{fulfillment_provider_name}</h4>
      <h4 className="sk:m-0 sk:ml-1">Fulfillment</h4>
      {fulfillment_provider_id === primaryWarehouse && (
        <Badge className="sk:ml-1">Primary</Badge>
      )}
      {!present_inventory && (
        <Badge tone="danger" className="sk:ml-1">
          OOS
        </Badge>
      )}
    </div>
  );
}

function VelocityQuickView({
  provider,
  setOriginSelected,
  isOriginSelected,
  isPrimary,
}) {
  const {
    displayName,
    fulfillment_provider_id,
    is_fulfillable,
    hybridProvider,
  } = provider;
  const isOutOfStock = provider.present_inventory < 1;

  const providerVelocityToUse = useMemo(
    () => hybridProvider || provider,
    [hybridProvider, provider],
  );

  const isAdjusted = useMemo(
    () =>
      CommonUtility.isNotNull(providerVelocityToUse.adjustedSalesVelocity) &&
      providerVelocityToUse.adjustedSalesVelocity !==
        providerVelocityToUse.providerSalesVelocity,
    [providerVelocityToUse],
  );

  if (!hybridProvider && !is_fulfillable) {
    return <></>;
  }

  return (
    <div
      onClick={() => setOriginSelected(fulfillment_provider_id)}
      className={cn(
        'sk:flex sk:h-[50px] sk:max-w-[300px] sk:min-w-[280px] sk:flex-1 sk:cursor-pointer sk:items-center sk:gap-x-[7px] sk:rounded-[5px] sk:px-[15px] sk:py-2.5 sk:text-dense',
        'sk:border sk:border-solid',
        isOriginSelected === fulfillment_provider_id
          ? 'sk:border-success sk:bg-success-bg sk:text-success'
          : 'sk:border-edge sk:bg-fill-tertiary',
      )}
    >
      {provider?.marketplaceRegionName && (
        <Flag
          width={30}
          height={20}
          className="sk:m-0"
          name={provider.marketplaceRegionName}
        />
      )}
      {!(isOutOfStock || isPrimary) && provider.is_FBA && (
        <img
          src="/images/marketplaces/fba.svg"
          alt="FBA"
          height={20}
          width={20}
        />
      )}
      <div className="sk:flex sk:flex-col">
        <div className="sk:flex sk:items-center">
          {(isOutOfStock || isPrimary) && provider.is_FBA && (
            <img
              src="/images/marketplaces/fba.svg"
              alt="FBA"
              height={15}
              width={15}
              className="sk:mr-1"
            />
          )}
          <DarkPopup
            position="bottom left"
            trigger={<span className={TRUNCATE}>{displayName}</span>}
          >
            <span>{displayName}</span>
          </DarkPopup>
        </div>
        <div className="sk:flex sk:items-center">
          {isPrimary && <SmallBadge className="sk:mr-2">Primary</SmallBadge>}
          {isOutOfStock && (
            <DarkPopup trigger={<SmallBadge tone="danger">OOS</SmallBadge>}>
              Out of Stock
            </DarkPopup>
          )}
        </div>
      </div>
      <div className="sk:ml-auto sk:whitespace-nowrap">
        {providerVelocityToUse.adjustedSalesVelocity ||
        providerVelocityToUse?.providerSalesVelocity ? (
          <div className="sk:flex sk:flex-col sk:items-end">
            <div>
              <span>
                {providerVelocityToUse.adjustedSalesVelocity ||
                  providerVelocityToUse?.providerSalesVelocity}
              </span>
              <MuteText className="sk:pl-1">Units / Day</MuteText>
            </div>
            {isAdjusted && (
              <div className="sk:-mt-[3px] sk:text-[11px]">
                <MuteText className="sk:pl-1">(Adjusted)</MuteText>
              </div>
            )}
          </div>
        ) : (
          'No Sales Velocity'
        )}
      </div>
    </div>
  );
}

function VelocityDetail({
  d,
  manualVelocities,
  oldManualVelocities,
  hardRefreshLoading,
  updateWindowVelocity,
  setSelectedVelocity,
  pushSelectedVelocity,
  selectedVelocity,
  loading,
  setManualInputValues,
  updateVelocity,
  updateManualVelocity,
  windowSelectedVelocity,
}) {
  const {
    adjustedSalesVelocity,
    fulfillment_provider_id: providerId,
    onlyMax,
    hybridProvider,
  } = useMemo(() => d, [d]);

  const orgVelocities = useMemo(() => {
    const objectToFetchFrom = hybridProvider || d || {};
    return Object.keys(objectToFetchFrom)?.filter(x =>
      onlyMax
        ? x.includes('overAllMax') || x.includes('Day')
        : x.includes('Day'),
    );
  }, [d, onlyMax, hybridProvider]);

  const primaryVelocities = useMemo(() => {
    const velocityObject = {};
    const objectToFetchFrom = hybridProvider || d || {};
    orgVelocities.forEach(velocity => {
      if (objectToFetchFrom[velocity]?.value) {
        velocityObject[velocity] = objectToFetchFrom[velocity];
      }
    });
    return velocityObject;
  }, [orgVelocities, d]);

  const actionButtonsFlags = useMemo(
    () => ({
      confirm: !!manualVelocities[providerId]?.changed,
      reset: !!(
        oldManualVelocities[providerId]?.value || windowSelectedVelocity
      ),
    }),
    [manualVelocities, providerId, oldManualVelocities, windowSelectedVelocity],
  );

  return (
    <React.Fragment key={providerId}>
      <div className="sk:flex sk:w-full sk:flex-col">
        <Velocities>
          {Object.keys(primaryVelocities || {}).map((velocity, index) => (
            <VelocityRenderer
              velocity={velocity}
              showSelection={1}
              index={index}
              providerVelocity={hybridProvider || d}
              // @ts-expect-error - TS2322: Type '{ velocity: string; showSelection: number; index: number; providerVelocity...
              updateWindowVelocity={updateWindowVelocity}
              selectedVelocity={selectedVelocity}
              selectVelocity={setSelectedVelocity}
              pushSelectedVelocity={pushSelectedVelocity}
              loading={hardRefreshLoading}
              adjustedSalesVelocity={adjustedSalesVelocity}
              oldManualVelocities={oldManualVelocities}
              providerId={providerId}
              hardRefreshLoading={hardRefreshLoading}
            />
          ))}
          <Velocity
            showSelection={!oldManualVelocities[providerId]?.value ? 1 : 0}
            active={oldManualVelocities[providerId]?.value}
            selected={
              selectedVelocity === Object.keys(primaryVelocities).length ? 1 : 0
            }
            onClick={() => {
              if (!loading) {
                !oldManualVelocities[providerId]?.value &&
                  setSelectedVelocity(Object.keys(primaryVelocities).length);
              }
            }}
            disabled={!!hardRefreshLoading}
            className="sk:items-center sk:p-0 sk:pl-2"
          >
            <div className="sk:flex sk:items-center">
              <RadioIndicator
                isSelected={!!oldManualVelocities[providerId]?.value}
              />
              <MuteText className="sk:ml-2">Manual</MuteText>
            </div>
            <div className="sk:ml-1 sk:flex sk:max-w-[150px] sk:items-center sk:gap-1">
              <NumberMaskInput
                aria-label="Manual units per day"
                className="sk:max-w-20"
                disabled={hardRefreshLoading}
                value={manualVelocities[providerId]?.value}
                onChange={value => {
                  setManualInputValues(providerId, value);
                }}
                placeholder="Units"
                maskOptions={{
                  allowDecimal: true,
                  // @ts-expect-error - TS2353: Object literal may only specify known properties, and 'decimalSymbol' does not e...
                  decimalSymbol: '.',
                  decimalLimit: 2,
                }}
              />
              <span className="sk:text-dense sk:whitespace-nowrap sk:text-fg-tertiary">
                / Day
              </span>
            </div>
          </Velocity>
        </Velocities>
        <div className="sk:mt-2">
          {actionButtonsFlags.confirm && (
            <StyledButton
              className="sk:ml-auto"
              disabled={hardRefreshLoading}
              onClick={() => updateVelocity(providerId)}
              size="mini"
            >
              Confirm
            </StyledButton>
          )}
          {actionButtonsFlags.reset && (
            <StyledButton
              className="sk:ml-auto"
              disabled={hardRefreshLoading}
              onClick={() => {
                if (oldManualVelocities[providerId]?.value) {
                  updateManualVelocity(
                    providerId,
                    manualVelocities[providerId]?.value,
                    true,
                  );
                } else {
                  updateWindowVelocity({
                    days: 'reset',
                    provider_id: providerId,
                  });
                }
              }}
              size="mini"
            >
              Reset
            </StyledButton>
          )}
        </div>
      </div>
    </React.Fragment>
  );
}

type VelocityProps = HTMLAttributes<HTMLDivElement> & {
  showSelection?: number;
  selected?: boolean | number;
  active?: boolean;
  disabled?: boolean;
};

function Velocity({
  showSelection,
  selected,
  active,
  disabled,
  className,
  onClick,
  ...rest
}: VelocityProps) {
  const selectable = showSelection === 1;
  return (
    <div
      {...rest}
      onClick={onClick}
      className={cn(
        'sk:relative sk:box-border sk:flex sk:min-h-[49.5px] sk:max-w-[300px] sk:min-w-[280px] sk:flex-1 sk:justify-between sk:rounded-[5px] sk:p-4',
        'sk:border sk:border-solid sk:border-border-subtle',
        selectable && 'sk:cursor-pointer',
        selectable &&
          selected &&
          'sk:border-2 sk:border-fg-tertiary sk:shadow-card',
        active &&
          'sk:border-2 sk:border-success sk:bg-success-bg sk:[&_b]:text-success',
        disabled && 'sk:cursor-not-allowed sk:opacity-80 sk:grayscale',
        className,
      )}
    />
  );
}

const FORMULA_PROVIDER = 'sk:whitespace-nowrap sk:[&_span]:whitespace-nowrap';

function CommonUnkownVelocityBreak({ provider }) {
  return (
    <>
      <div className={cn(FORMULA_PROVIDER, 'sk:mt-2')}>
        <span className="sk:italic">Unknown</span>
        <MuteText className="sk:mx-2">=</MuteText>
        {Object.values(provider.unknownProviderBreakdown).map(
          (marketplace, index) => (
            <>
              {index !== 0 ? <MuteText className="sk:mx-2">+</MuteText> : ''}
              <span>
                {CommonUtility.removeUnderscoreAndCapitalize(
                  // @ts-expect-error - TS18046: 'marketplace' is of type 'unknown'.
                  marketplace.marketplaceName,
                )}
              </span>
            </>
          ),
        )}
      </div>
      <div className={FORMULA_PROVIDER}>
        <span>{provider.value}</span>
        <MuteText className="sk:ml-1">/ Day</MuteText>
        <MuteText className="sk:mx-2">=</MuteText>
        {Object.values(provider.unknownProviderBreakdown || {}).map(
          (marketplace, index) => (
            <>
              {index !== 0 ? <MuteText className="sk:mx-2">+</MuteText> : ''}
              {/* @ts-expect-error - TS18046: 'marketplace' is of type 'unknown'. */}
              <span>{marketplace.value}</span>
              <MuteText className="sk:ml-1">/ Day</MuteText>
            </>
          ),
        )}
      </div>
    </>
  );
}

function PrimaryProviderBreakdown({
  provider,
  filteredArray,
  hybridProvider = false,
}) {
  const hasUnknown = useMemo(
    () => filteredArray.find(x => x.unknownProvider),
    [filteredArray],
  );

  const ProviderArray = useMemo(() => {
    if (hybridProvider) {
      return [...(filteredArray || []), hybridProvider];
    }
    return filteredArray;
  }, [hybridProvider, filteredArray]);

  return (
    <div>
      The primary warehouse velocity is the aggregate sum of the underlying
      fulfillment centers that it restocks.
      {ProviderArray?.length > 0 ? (
        <>
          <div className={cn(FORMULA_PROVIDER, 'sk:mt-1')}>
            {provider.fulfillment_provider_name}
            <MuteText className="sk:ml-1">(Unified Velocity)</MuteText>
            <MuteText className="sk:mx-2">=</MuteText>
            {ProviderArray?.map((underlyingProvider, index) => (
              <span>
                {index !== 0 ? <MuteText className="sk:mx-2">+</MuteText> : ''}
                <span
                  className={
                    underlyingProvider.unknownProvider ? 'sk:italic' : ''
                  }
                >
                  {underlyingProvider.fulfillment_provider_name}
                </span>
              </span>
            ))}
          </div>
          <div className={FORMULA_PROVIDER}>
            {provider.adjustedSalesVelocity || provider.providerSalesVelocity}
            <MuteText className="sk:ml-1">/ Day</MuteText>
            <MuteText className="sk:mx-2">=</MuteText>
            {ProviderArray?.map((underlyingProvider, index) => (
              <span>
                {index !== 0 ? <MuteText className="sk:mx-2">+</MuteText> : ''}
                {underlyingProvider.adjustedSalesVelocity ||
                  underlyingProvider.providerSalesVelocity}
                <MuteText className="sk:ml-1">/ Day</MuteText>
              </span>
            ))}
          </div>
          {!!hasUnknown && <CommonUnkownVelocityBreak provider={hasUnknown} />}
        </>
      ) : (
        '.'
      )}
    </div>
  );
}

function VelocityRenderer({
  velocity,
  showSelection,
  providerVelocity,
  selectVelocity,
  pushSelectedVelocity,
  selectedVelocity,
  index,
  loading,
  adjustedSalesVelocity,
  oldManualVelocities,
  providerId,
  hardRefreshLoading,
}) {
  const setSelectedVelocity = () => {
    if (loading || !showSelection) {
      return;
    }

    if (active) {
      selectVelocity(index, providerId);
      return;
    }
    pushSelectedVelocity(index, providerId, providerVelocity);
  };

  const salesVelocity = useMemo(() => {
    const velocityObject = providerVelocity[velocity] || {};

    if (velocity === 'overAllMax') {
      velocityObject.days = 'Adjusted';
    } else if (adjustedSalesVelocity && velocityObject.isPrimary) {
      velocityObject.value = adjustedSalesVelocity;
    }
    return velocityObject;
  }, [providerVelocity, velocity, adjustedSalesVelocity]);

  const active = useMemo(
    () =>
      !oldManualVelocities[providerId]?.value &&
      salesVelocity?.isPrimary &&
      showSelection &&
      !hardRefreshLoading,
    [oldManualVelocities, salesVelocity, showSelection, hardRefreshLoading],
  );

  const isActive = useMemo(
    () =>
      (active && selectedVelocity < 0) ||
      (showSelection && selectedVelocity === index),
    [active, selectedVelocity, showSelection, index],
  );

  const isAdjusted = useMemo(() => {
    const { providerSalesVelocities = {}, adjustedSalesVelocity } =
      providerVelocity ?? {};

    if (!adjustedSalesVelocity) {
      return false;
    }

    const data = Object.keys(providerSalesVelocities).filter(d =>
      d.includes('Day'),
    );

    if (CommonUtility.isValidArray(data)) {
      const primary = data.find(d => providerSalesVelocities[d]?.isPrimary);

      if (primary) {
        return false;
      }

      let maxKey = '';
      let maxValue = 0;
      data.forEach(d => {
        if (
          providerSalesVelocities[d]?.value &&
          providerSalesVelocities[d]?.value > maxValue
        ) {
          maxValue = providerSalesVelocities[d].value;
          maxKey = d;
        }
      });

      return maxKey === velocity;
    }

    return false;
  }, [providerVelocity, velocity]);

  return (
    <Velocity
      onClick={setSelectedVelocity}
      key={velocity}
      active={isActive}
      selected={selectedVelocity === index ? 1 : 0}
      showSelection={showSelection}
      disabled={!!loading}
    >
      <div className="sk:flex sk:items-center">
        {showSelection ? (
          <RadioIndicator className="sk:mr-2" isSelected={!!isActive} />
        ) : (
          <></>
        )}
        <MuteText>
          {salesVelocity.days} {salesVelocity.days !== 'Adjusted' ? 'Days' : ''}
        </MuteText>
        {providerVelocity.adjustedSalesVelocity === salesVelocity.value ||
        isAdjusted ? (
          <MuteText className="sk:ml-2 sk:self-center sk:text-xs sk:font-normal">
            (Adjusted)
          </MuteText>
        ) : (
          ''
        )}
      </div>
      <b>{salesVelocity.value} Units / Day</b>
    </Velocity>
  );
}
